// KoachSignupStep2: collects basic personal info & profile image for koach signup.
import React, { useEffect } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import * as commonData from "data/common.data";
import { getCurrentLangName } from "helpers/String.helper";

interface KoachSignupStep2Props {
    personalParams: {
        countryCode: string;
        firstName: string;
        lastName: string;
        genderTypeCode: string;
    };
    handleInputChange: (section: string, field: string, value: any) => void;
    onValidationChange?: (isValid: boolean) => void;
    onProfileImageFileChange?: (evt: React.ChangeEvent<HTMLInputElement>) => void;
    profileImageInfo?: {
        id?: string;
        url?: string;
    } | null;
}

const KoachSignupStep2: React.FC<KoachSignupStep2Props> = ({ 
    personalParams, 
    handleInputChange, 
    onValidationChange,
    onProfileImageFileChange,
    profileImageInfo
}) => {
    // 모든 필수 필드가 입력되었는지 확인
    const isAllRequiredFieldsFilled = (): boolean => {
        return (
            personalParams.countryCode.trim() !== "" && // 국적 선택
            personalParams.firstName.trim() !== "" && // 이름 입력
            personalParams.lastName.trim() !== "" && // 성 입력
            personalParams.genderTypeCode.trim() !== "" && // 성별 입력
            !!profileImageInfo?.id // 프로필 사진 등록 완료
        );
    };

    // 유효성 검사 상태를 부모 컴포넌트에 전달
    useEffect(() => {
        const isValid = isAllRequiredFieldsFilled();
        onValidationChange?.(isValid);
    }, [personalParams, profileImageInfo]);

    return (
        <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
            <div className="p-6">
                <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.title")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.desc")}
                    </p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span>
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.country")}
                        </label>
                        <select
                            value={personalParams.countryCode}
                            onChange={(evt) => handleInputChange("personal", "countryCode", evt.target.value)}
                            className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                        >
                            <option value="" disabled selected>
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.countryPlaceholder")}
                            </option>
                            {commonData.getCountryCodeList().map((item: any, idx: number) => (
                                <option key={idx} value={item.code}>
                                    {getCurrentLangName(item.names)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <span className="text-red-500">*</span>
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.name")}
                            </label>
                            <input
                                type="text"
                                value={personalParams.firstName}
                                onChange={(evt) => handleInputChange("personal", "firstName", evt.target.value)}
                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.namePlaceholder")}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <span className="text-red-500">*</span>
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.lastName")}
                            </label>
                            <input
                                type="text"
                                value={personalParams.lastName}
                                onChange={(evt) => handleInputChange("personal", "lastName", evt.target.value)}
                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.lastNamePlaceholder")}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span>
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.gender")}
                        </label>
                        <div className="flex gap-6">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="genderTypeCode"
                                    value="genderType:girl"
                                    checked={personalParams.genderTypeCode === "genderType:girl"}
                                    onChange={(evt) => handleInputChange("personal", "genderTypeCode", evt.target.value)}
                                    className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688]"
                                />
                                <span className="ml-2 text-gray-700">
                                    {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.genderFemale")}
                                </span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="genderTypeCode"
                                    value="genderType:boy"
                                    checked={personalParams.genderTypeCode === "genderType:boy"}
                                    onChange={(evt) => handleInputChange("personal", "genderTypeCode", evt.target.value)}
                                    className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688]"
                                />
                                <span className="ml-2 text-gray-700">
                                    {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.genderMale")}
                                </span>
                            </label>
                        </div>
                        {!personalParams.genderTypeCode && (
                            <p className="mt-2 text-sm text-red-600">
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.genderErrorNoInput")}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span>
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.profileImage")}
                        </label>
                        <div className="flex justify-center">
                            <div className="relative w-40 h-40">
                                <div
                                    className="w-full h-full rounded-full bg-center bg-cover border-2 border-gray-200"
                                    style={{
                                        backgroundImage: `url(${profileImageInfo?.url || "./../../imgs/icons/camera.png"})`
                                    }}
                                />
                                <input
                                    type="file"
                                    id="input-file-img-profile"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={(evt) => onProfileImageFileChange?.(evt)}
                                />
                                <label
                                    htmlFor="input-file-img-profile"
                                    className="absolute bottom-0 right-0 bg-[#CF3688] text-white p-2 rounded-full cursor-pointer hover:bg-[#B52E74] transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                    </svg>
                                </label>
                            </div>
                        </div>
                        {!profileImageInfo?.id && (
                            <p className="mt-2 text-sm text-red-600 text-center">
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep2.profileImageErrorNoInput")}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default KoachSignupStep2; 